import {
    DataTexture,
    FloatType,
    MagnificationTextureFilter,
    Mapping,
    Mesh,
    MinificationTextureFilter,
    OrthographicCamera,
    PixelFormat,
    PlaneGeometry,
    RepeatWrapping,
    Scene,
    Texture,
    TextureDataType,
    TextureEncoding,
    WebGLRenderer,
    WebGLRenderTarget,
    Wrapping
} from "three";
import {
    TextureScaleMaterialFactory
} from "features/apartmentViewer/ApartmentViewer/shaders/textureScaleMaterial/TextureScaleMaterialFactory";

interface DataTextureOptions {
    wrapS?: Mapping;
    wrapT?: Wrapping;
    magFilter?: MagnificationTextureFilter;
    minFilter?: MinificationTextureFilter;
    anisotropy?: number;
    encoding?: TextureEncoding;
    flipY?: boolean;
}

export class TextureHelper {
    texturesToNullify: Set<DataTexture> = new Set();
    camera: OrthographicCamera = new OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 1);
    scene: Scene = new Scene();

    constructor(
        private renderer: WebGLRenderer
    ) { }


    // Create a new data texture
    createDataTexture (
        data: BufferSource,
        width: number,
        height: number,
        format: PixelFormat,
        type: TextureDataType,
        options: DataTextureOptions = {}
    ): DataTexture {
        const texture = new DataTexture(data, width, height, format, type);


        Object.assign(texture, options);

        texture.needsUpdate = true;
        this.texturesToNullify.add(texture);
        this.scheduleTextureDataCleanup();
        return texture;
    }

    scheduleTextureDataCleanup(): void {
        const clearData = () => {
            for (const texture of this.texturesToNullify) {
                if (texture.image && texture.image.data) {
                    texture.image.data = null;
                }
                texture.needsUpdate = false;
                this.texturesToNullify.delete(texture);
            }
        };
        setTimeout(clearData, 16); // Approximate to one frame (16 ms)
    }

    // Scale a texture to a new width and height using a fast method
    scaleTexture(texture: Texture, width: number, height: number): WebGLRenderTarget {
        const material = TextureScaleMaterialFactory.createMaterial(texture);
        const quad = new Mesh(new PlaneGeometry(1, 1), material);
        this.scene.add(quad);

        const renderTarget = new WebGLRenderTarget(width, height, {
            type: FloatType,
            wrapS: RepeatWrapping,
            wrapT: RepeatWrapping,
        });

        try {
            this.renderer.setRenderTarget(renderTarget);
            this.renderer.render(this.scene, this.camera);
        } finally {
            this.renderer.setRenderTarget(null);
            this.scene.remove(quad);
            quad.geometry.dispose();
            material.dispose();
        }

        return renderTarget;
    }

}
